import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Linkedin, Mail, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

var footerLinks = {
  solutions: [
    { label: 'Data Engineering', href: '/solutions/data-engineering' },
    { label: 'Reporting & Assurance', href: '/solutions/reporting' },
    { label: 'dMRV Solution', href: '/solutions/dmrv' },
    { label: 'Tokenization & Blockchain', href: '/solutions/tokenization' },
    { label: 'Credibility Engine', href: '/solutions/credibility-engine' },
    { label: 'SocTech', href: '/solutions/soctech' },
  ],
  services: [
    { label: 'Materiality Assessment', href: '/services/materiality-assessment' },
    { label: 'ESG Reporting & Compliance', href: '/services/esg-reporting' },
    { label: 'ESG Outsourcing', href: '/services/esg-outsourcing' },
    { label: 'Strategy & Program Design', href: '/services/strategy-program-design' },
    { label: 'Data Curation', href: '/services/data-curation' },
    { label: 'Training & Enablement', href: '/services/training-enablement' },
  ],
  industries: [
    { label: 'IT-BPM', href: '/industries/itbpm' },
    { label: 'Real Estate', href: '/industries/real-estate' },
    { label: 'Manufacturing', href: '/industries/manufacturing' },
    { label: 'Logistics', href: '/industries/logistics' },
    { label: 'Hospitality', href: '/industries/hospitality' },
    { label: 'Public Sector', href: '/industries/public-sector' },
  ],
  company: [
    { label: 'About', href: '/about' },
    { label: 'Case Studies', href: '/case-studies' },
    { label: 'Partners', href: '/partners' },
    { label: 'Insights', href: '/insights' },
    { label: 'Careers', href: '/careers' },
  ],
  resources: [
    { label: 'Contact', href: '/contact' },
    { label: 'Book a Call', href: '/book-call' },
    { label: 'Privacy Policy', href: '/privacy' },
  ],
};

// Custom link component that scrolls to top
function FooterLink({ to, children, className }) {
  const navigate = useNavigate();
  
  function handleClick(e) {
    e.preventDefault();
    window.scrollTo(0, 0);
    navigate(to);
  }
  
  return (
    <a
      href={to}
      onClick={handleClick}
      className={className}
    >
      {children}
    </a>
  );
}

function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  async function handleNewsletterSubmit(e) {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }

      toast.success('Thank you for subscribing!');
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <footer className="bg-navy text-navy-foreground">
      <div className="border-b border-navy-foreground/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-3">
                Build sustainability credibility that investors trust.
              </h2>
              <p className="text-navy-foreground/70 text-lg">
                Start your journey to finance-grade sustainability data today.
              </p>
            </div>
            <FooterLink to="/book-call">
              <Button variant="teal" size="xl" className="group">
                Book a Discovery Call
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </FooterLink>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8 lg:gap-8">
          <div className="col-span-2 lg:col-span-2">
            <FooterLink to="/" className="inline-block mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_1f762a5e-4e34-4452-aabe-298bcfb2d13e/artifacts/9emip28a_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg"
                alt="SusTek"
                className="h-10 w-auto brightness-0 invert opacity-90"
              />
            </FooterLink>
            <p className="text-navy-foreground/70 text-sm leading-relaxed mb-6 max-w-xs">
              SusTek engineers trusted sustainability data systems and credibility engines for
              reporting and sustainability finance.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-navy-foreground/10 border-navy-foreground/20 text-navy-foreground placeholder:text-navy-foreground/50 flex-1"
                disabled={isSubmitting}
                required
              />
              <Button type="submit" variant="teal" size="icon" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">Solutions</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map(function(link) {
                return (
                  <li key={link.href}>
                    <FooterLink
                      to={link.href}
                      className="text-sm text-navy-foreground/70 hover:text-navy-foreground transition-colors"
                    >
                      {link.label}
                    </FooterLink>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map(function(link) {
                return (
                  <li key={link.href}>
                    <FooterLink
                      to={link.href}
                      className="text-sm text-navy-foreground/70 hover:text-navy-foreground transition-colors"
                    >
                      {link.label}
                    </FooterLink>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">Industries</h4>
            <ul className="space-y-3">
              {footerLinks.industries.map(function(link) {
                return (
                  <li key={link.href}>
                    <FooterLink
                      to={link.href}
                      className="text-sm text-navy-foreground/70 hover:text-navy-foreground transition-colors"
                    >
                      {link.label}
                    </FooterLink>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map(function(link) {
                return (
                  <li key={link.href}>
                    <FooterLink
                      to={link.href}
                      className="text-sm text-navy-foreground/70 hover:text-navy-foreground transition-colors"
                    >
                      {link.label}
                    </FooterLink>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map(function(link) {
                return (
                  <li key={link.href}>
                    <FooterLink
                      to={link.href}
                      className="text-sm text-navy-foreground/70 hover:text-navy-foreground transition-colors"
                    >
                      {link.label}
                    </FooterLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <Separator className="my-12 bg-navy-foreground/10" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-navy-foreground/60">
            {new Date().getFullYear()} SusTek. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.linkedin.com/company/sustek-ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy-foreground/60 hover:text-navy-foreground transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-5 w-5" />
            </a>
            <a
              href="mailto:contactus@sustek.ai"
              className="text-navy-foreground/60 hover:text-navy-foreground transition-colors"
              aria-label="Email"
            >
              <Mail className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
